/**
 * ConnectionOverlay - Reconnect UX overlay for WebSocket connection issues.
 *
 * Two states:
 * - "reconnecting": Semi-transparent overlay with spinner "Ponowne łączenie..."
 * - "failed": Opaque overlay "Utracono połączenie" with "Odśwież stronę" button
 */

import { useTranslation } from "react-i18next"

import { PixelIcon } from "./PixelIcon"

interface ConnectionOverlayProps {
  state: "reconnecting" | "failed"
}

/**
 * Decorative test-card stripe, echoing an analogue TV that lost its signal.
 * Purely visual, so it carries no text and stays out of the accessibility tree.
 */
function TestCardStripe() {
  return (
    <div className="flex h-6 w-full" aria-hidden="true">
      <div className="flex-1 bg-secondary" />
      <div className="flex-1 bg-primary" />
      <div className="flex-1 bg-foreground" />
      <div className="flex-1 bg-destructive" />
      <div className="flex-1 bg-muted" />
      <div className="flex-1 bg-pixel-outline" />
    </div>
  )
}

export function ConnectionOverlay({ state }: ConnectionOverlayProps) {
  const { t } = useTranslation()

  if (state === "reconnecting") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="flex w-[min(90vw,24rem)] flex-col items-center gap-6 bg-card p-8 pixel-frame-lg">
          <TestCardStripe />

          {/* Blinking block stands in for a spinner: rotation has no pixel form */}
          <div className="h-6 w-6 bg-primary animate-pixel-blink" />

          <p className="font-display text-lg font-bold text-foreground tracking-wide text-center">
            {t("connection.reconnecting")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="flex w-[min(90vw,24rem)] flex-col items-center gap-6 bg-card p-8 pixel-frame-lg">
        <TestCardStripe />
        <PixelIcon className="text-4xl">⚠️</PixelIcon>
        <p className="font-display text-lg font-bold text-foreground tracking-wide text-center">
          {t("connection.lost")}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-primary px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-primary-foreground pixel-frame pixel-press hover:bg-primary-hover"
        >
          {t("connection.refresh")}
        </button>
      </div>
    </div>
  )
}
